import { AsyncPipe, CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { Store } from "@ngxs/store";
import { TabsetComponent, TabsModule } from "ngx-bootstrap/tabs";
import { Observable, Subscription } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import { PlannerIndicatorDirective } from "../shared/components/plannerindicator.component";
import { StatusButtonGroupComponent } from "../shared/components/status-button-group.component";
import { IAssessmentResultDisplaySettings } from "../shared/model/interfaces";
import {
  AssignmentDetailsState,
  AssignmentDetailsStateModel,
  HideAssignmentAction,
  HideLogAction,
  LoadAssessmentResultsAction,
  LoadReviewUrlAction,
  ResultData,
  ReviewUrls,
  SelectAssessmentResultAction,
  ShowAssignmentAction,
  ShowLogAction,
} from "../shared/store/assignment-details.state";
import {
  PlanningState,
  SetSelectedAssignmentIdAction,
} from "../shared/store/planning.state";
import { Assignment, AssignmentStatus } from "./../shared/model/model";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-assignment-details",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    AsyncPipe,
    TabsModule,
    PlannerIndicatorDirective,
    StatusButtonGroupComponent,
  ],
  templateUrl: "./assignment-details.component.html",
  styleUrls: ["./assignment-details.component.scss"],
})
export class AssignmentDetailsComponent implements OnDestroy {
  @ViewChild("icon", { static: true }) el: ElementRef;
  @ViewChild("staticTabs", { static: true }) staticTabs: TabsetComponent;
  stateSubscription: Subscription;
  assignmentId: string;
  assignmentDetailsState$: Observable<AssignmentDetailsStateModel>;
  selectedResult$: Observable<ResultData>;
  assignment$: Observable<Assignment>;
  displaySettings$: Observable<IAssessmentResultDisplaySettings>;
  actionsTop = 0;
  actionsLeft = 0;
  correctUrl: SafeResourceUrl;
  reviewUrl: SafeResourceUrl;
  previousUrls: ReviewUrls;

  constructor(
    private store: Store,
    public domSanitizer: DomSanitizer,
    private router: Router,
    route: ActivatedRoute
  ) {
    this.assignmentDetailsState$ =
      route.params.pipe<AssignmentDetailsStateModel>(
        mergeMap((params) => {
          this.assignmentId = params["id"];
          this.store.dispatch(
            new SetSelectedAssignmentIdAction(this.assignmentId)
          );
          this.store.dispatch(new HideLogAction());
          this.store.dispatch(
            new LoadAssessmentResultsAction(this.assignmentId)
          );
          return this.store.select(
            AssignmentDetailsState as any
          ) as Observable<AssignmentDetailsStateModel>;
        })
      );
    this.assignment$ = this.store.select(PlanningState.selectedAssignment);
    this.selectedResult$ = this.store.select(
      AssignmentDetailsState.selectedResult
    );
    this.displaySettings$ = this.store
      .select(AssignmentDetailsState.displaySetting)
      .pipe(
        map((setting) => {
          this.actionsTop =
            this.el && this.el.nativeElement
              ? this.el.nativeElement.getBoundingClientRect().top + 22
              : 0;
          this.actionsLeft =
            this.el && this.el.nativeElement
              ? this.el.nativeElement.getBoundingClientRect().left - 120
              : 0;
          return setting;
        })
      );
    this.stateSubscription = this.store
      .select(AssignmentDetailsState.urls)
      .subscribe((urls) => {
        if (
          (urls && !this.previousUrls) ||
          (urls &&
            this.previousUrls &&
            (this.previousUrls.reviewUrl !== urls.reviewUrl ||
              this.previousUrls.correctUrl !== urls.correctUrl))
        ) {
          this.correctUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
            urls.correctUrl
          );
          this.reviewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
            urls.reviewUrl
          );
          this.previousUrls = urls;
        }
      });
  }
  ngOnDestroy(): void {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }
  }

  showAssessmentResult = (status: AssignmentStatus, testSessionId: string) => {
    if (
      status === AssignmentStatus.Failed ||
      (status === AssignmentStatus.Passed && testSessionId)
    ) {
      this.store.dispatch(new SelectAssessmentResultAction(testSessionId));
      this.store.dispatch(new HideLogAction());
      this.staticTabs.tabs[0].active = true;
    }
  };
  hideActions = (event: any) => {
    if (event.target !== this.el.nativeElement) {
      this.store.dispatch(new HideAssignmentAction());
    }
  };

  showActions = () => this.store.dispatch(new ShowAssignmentAction());
  showLog = () => this.store.dispatch(new ShowLogAction());
  hideLog = () => this.store.dispatch(new HideLogAction());
  reviewItem = (id: string) => this.store.dispatch(new LoadReviewUrlAction(id));
  close = () => this.router.navigate(["plan"]);
}
